import classnames from 'classnames'
import { DateTime } from 'luxon'
import type { FC } from 'react'
import Skeleton from 'react-loading-skeleton'
import type { WithStyles } from 'react-jss'
import withStyles from 'react-jss'
import type { SaladTheme } from '../../../../SaladTheme'
import { renderQuoteDisplayDecimals } from '../../../render'

const styles = (theme: SaladTheme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  containerCheckout: {
    alignItems: 'flex-start',
    paddingTop: 6,
  },
  amountText: {
    // Book weight (rather than the thin Light09) and the header's light-green keep the token amount clearly legible
    // against the dark reward-detail header.
    color: theme.lightGreen,
    fontFamily: theme.fontGroteskBook25,
    fontSize: 18,
    letterSpacing: 0.5,
  },
  freshnessText: {
    fontFamily: theme.fontGroteskBook25,
    fontSize: 10,
    letterSpacing: 1,
    color: theme.lightGreen,
    opacity: 0.85,
    textTransform: 'uppercase',
  },
  // Checkout (order summary) renders on a white modal, so the green used on the dark detail header is illegible there.
  // A darker green and larger type keep the exchange-rate amount and "quoted at" line readable.
  amountTextCheckout: {
    color: theme.darkGreen,
    fontFamily: theme.fontGroteskMedium25,
    fontSize: 20,
  },
  freshnessTextCheckout: {
    color: theme.darkGreen,
    fontSize: 12,
    opacity: 1,
  },
  staleText: {
    color: theme.orange,
    opacity: 1,
  },
  errorText: {
    fontFamily: theme.fontGroteskBook25,
    fontSize: 10,
    letterSpacing: 1,
    color: theme.lightGreen,
    opacity: 0.85,
  },
  errorTextCheckout: {
    color: theme.darkGreen,
    fontSize: 12,
    opacity: 1,
  },
  skeleton: {
    width: 90,
  },
})

// Both variants show how many RENDER tokens the reward's Salad Balance cost converts to (e.g. `≈ 1.0003 RENDER`),
// rounded to the display precision. RENDER carries more on-chain precision than is useful to show, so the amount is
// rounded and a trailing-zero-free format keeps whole amounts tidy (e.g. `≈ 5 RENDER`).
const renderTokenFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: renderQuoteDisplayDecimals,
})

export interface RenderPriceQuoteProps extends WithStyles<typeof styles> {
  /** Whether a quote request is currently in flight and no quote is available yet. */
  loading?: boolean
  /** Whether the most recent quote request failed. */
  error?: boolean
  /** Whether the currently displayed quote is stale. */
  stale?: boolean
  /** The number of RENDER tokens the reward's Salad Balance cost converts to. */
  tokenAmount?: number
  /** When the displayed quote was generated. */
  asOf?: Date
  /** Controls layout/labelling for the detail header vs. the checkout review surface. */
  variant?: 'detail' | 'checkout'
}

const _RenderPriceQuote: FC<RenderPriceQuoteProps> = ({
  classes,
  loading,
  error,
  stale,
  tokenAmount,
  asOf,
  variant = 'detail',
}) => {
  const isCheckout = variant === 'checkout'
  const containerClass = isCheckout ? `${classes.container} ${classes.containerCheckout}` : classes.container
  const amountClass = isCheckout ? `${classes.amountText} ${classes.amountTextCheckout}` : classes.amountText
  const errorClass = isCheckout ? `${classes.errorText} ${classes.errorTextCheckout}` : classes.errorText

  if (loading && tokenAmount === undefined) {
    return (
      <div className={containerClass}>
        <div className={classes.skeleton}>
          <Skeleton />
        </div>
      </div>
    )
  }

  if (error && tokenAmount === undefined) {
    return (
      <div className={containerClass}>
        <div className={errorClass}>RENDER price unavailable</div>
      </div>
    )
  }

  if (tokenAmount === undefined) {
    return null
  }

  const asOfLabel = asOf ? DateTime.fromJSDate(asOf).toRelative() : undefined
  const freshnessText = stale
    ? 'Price may be out of date'
    : asOfLabel
    ? `Quoted ${asOfLabel}`
    : undefined

  const freshnessClass = classnames(classes.freshnessText, {
    [classes.freshnessTextCheckout]: isCheckout,
    [classes.staleText]: stale,
  })

  // Both variants surface how much RENDER the Chef will receive for the reward's Salad Balance cost, with a smaller
  // freshness line ("quoted N seconds ago" / a stale warning) directly beneath it. Only the layout/styling differs
  // between the dark detail header and the white checkout modal.
  const receiveLabel = `You'll receive ≈ ${renderTokenFormatter.format(tokenAmount)} RENDER`

  return (
    <div className={containerClass}>
      <div className={amountClass}>{receiveLabel}</div>
      {freshnessText && <div className={freshnessClass}>{freshnessText}</div>}
    </div>
  )
}

export const RenderPriceQuote = withStyles(styles)(_RenderPriceQuote)
